import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Card,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import type React from 'react';
import { useSnackbar } from '../../contexts/SnackbarContext';
import type { TemplateTypeConfig } from '../../types/template';

interface TagReferenceProps {
  config: TemplateTypeConfig;
  type?: string;
}

const EMPTY_ARRAY: never[] = [];

const TagReference: React.FC<TagReferenceProps> = ({ config, type }) => {
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();

  const handleCopy = (tag: string) => {
    navigator.clipboard.writeText(tag);
    showSnackbar(`Copied ${tag} to clipboard`, 'success');
  };

  const isCV = config.label === 'CV Template' || type === 'cv';

  const renderTagList = (
    title: string,
    tags: string[],
    variant: 'required' | 'suggested' | 'optional'
  ) => {
    if (!tags || tags.length === 0) {
      return null;
    }

    let titleStyle = {};
    if (variant === 'required') {
      titleStyle = {
        color: '#ffffff',
        backgroundColor: theme.palette.bidlogiq.brick,
        padding: '4px 8px',
        borderRadius: '4px',
        display: 'inline-block',
      };
    } else if (variant === 'suggested') {
      titleStyle = {
        color: theme.palette.primary.main,
        paddingLeft: '8px',
        borderLeft: `4px solid ${theme.palette.primary.main}`,
      };
    } else {
      titleStyle = {
        color: theme.palette.text.secondary,
      };
    }

    return (
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1,
            fontWeight: 'bold',
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            ...titleStyle,
          }}
        >
          {title}
        </Typography>
        <Card variant="outlined" sx={{ backgroundColor: theme.palette.background.default }}>
          <List dense disablePadding>
            {tags.map((tag, index) => {
              const fieldDef = config.allowedTags.find((t) => t.tag === tag);
              return (
                <ListItem
                  key={tag}
                  divider={index < tags.length - 1}
                  secondaryAction={
                    !isCV ? (
                      <Tooltip title="Copy Tag">
                        <IconButton
                          edge="end"
                          aria-label="copy"
                          onClick={() => handleCopy(tag)}
                          size="small"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null
                  }
                >
                  <ListItemText
                    primary={
                      <Box
                        component="span"
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          fontSize: '1rem',
                          color: '#ffffff',
                          backgroundColor: theme.palette.bidlogiq?.black || '#333333',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          whiteSpace: 'pre-wrap',
                          display: 'inline-block',
                        }}
                      >
                        {tag}
                      </Box>
                    }
                    secondary={fieldDef?.description || 'Template Field'}
                    primaryTypographyProps={{ sx: { mb: 1 } }}
                    secondaryTypographyProps={{
                      variant: 'body1',
                      color: 'text.secondary',
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Card>
      </Box>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: '100%',
        overflowY: 'auto',
        backgroundColor: theme.palette.greyLight,
        borderLeft: `1px solid ${theme.palette.greyContrast}`,
      }}
    >
      <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary }}>
        Tag Reference
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Copy these tags into your Word document to indicate where content should be placed.
      </Typography>

      <Divider sx={{ mb: 2 }} />

      {renderTagList('Required Tags', config.requiredTags, 'required')}
      {renderTagList('Suggested Tags', config.suggestedTags || EMPTY_ARRAY, 'suggested')}
      {renderTagList('Optional Tags', config.optionalTags, 'optional')}
    </Paper>
  );
};

export default TagReference;
